import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const CODE_SNIPPETS = [
  // Short snippets
  "const greeting = 'Hello World';",
  "function add(a, b) { return a + b; }",
  "const arr = [1, 2, 3].map(x => x * 2);",
  "import React from 'react';",
  
  // Medium snippets
  "const isValid = value !== null && value !== undefined;",
  "async function fetchData() { const res = await fetch(url); return res.json(); }",
  "export default function App() { return <div>Hello World</div>; }",
  "const users = data.filter(user => user.active).map(user => user.name);",
  "const debounce = (fn, delay) => { let timer; return (...args) => { clearTimeout(timer); timer = setTimeout(() => fn(...args), delay); }; }",
  
  // Longer snippets
  "const fibonacci = (n) => { if (n <= 1) return n; return fibonacci(n - 1) + fibonacci(n - 2); }",
  "class Stack { constructor() { this.items = []; } push(element) { this.items.push(element); } pop() { return this.items.pop(); } }",
  "const quickSort = (arr) => { if (arr.length <= 1) return arr; const pivot = arr[0]; const left = arr.slice(1).filter(x => x < pivot); const right = arr.slice(1).filter(x => x >= pivot); return [...quickSort(left), pivot, ...quickSort(right)]; }",
  
  // React/Modern JS patterns
  "const [count, setCount] = useState(0); useEffect(() => { document.title = 'Count: ' + count; }, [count]);",
  "const fetchUser = async (id) => { try { const response = await fetch('/api/users/' + id); const data = await response.json(); return data; } catch (error) { console.error('Error:', error); } }",
  "const TodoItem = ({ todo, onToggle }) => { return <div onClick={() => onToggle(todo.id)} className={todo.completed ? 'done' : ''}>{todo.text}</div>; }",
  
  // Advanced patterns
  "const compose = (...fns) => x => fns.reduceRight((acc, fn) => fn(acc), x);",
  "const memoize = (fn) => { const cache = {}; return (...args) => { const key = JSON.stringify(args); if (key in cache) return cache[key]; const result = fn(...args); cache[key] = result; return result; }; }",
  "const deepClone = (obj) => { if (obj === null || typeof obj !== 'object') return obj; const clone = Array.isArray(obj) ? [] : {}; for (let key in obj) { clone[key] = deepClone(obj[key]); } return clone; }",
  
  // Real-world examples
  "const apiClient = { get: (url) => fetch(url).then(res => res.json()), post: (url, data) => fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }).then(res => res.json()) }",
  "const validateEmail = (email) => { const regex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,6}$/; return regex.test(email); }",
  "const throttle = (func, limit) => { let inThrottle; return function() { const args = arguments; const context = this; if (!inThrottle) { func.apply(context, args); inThrottle = true; setTimeout(() => inThrottle = false, limit); } } }",
  
  // TypeScript examples
  "interface User { id: number; name: string; email: string; role: 'admin' | 'user'; }",
  "type Result<T> = { success: true; data: T } | { success: false; error: string };",
  "const processUser = <T extends User>(user: T): string => { return user.name + ' (' + user.email + ')'; }",
  
  // Longer algorithmic examples
  "const binarySearch = (arr, target) => { let left = 0, right = arr.length - 1; while (left <= right) { const mid = Math.floor((left + right) / 2); if (arr[mid] === target) return mid; if (arr[mid] < target) left = mid + 1; else right = mid - 1; } return -1; }",
  "const mergeSortedArrays = (arr1, arr2) => { let result = [], i = 0, j = 0; while (i < arr1.length && j < arr2.length) { if (arr1[i] < arr2[j]) result.push(arr1[i++]); else result.push(arr2[j++]); } return result.concat(arr1.slice(i)).concat(arr2.slice(j)); }",
  
  // React Hooks examples
  "const useLocalStorage = (key, initialValue) => { const [value, setValue] = useState(() => { const item = localStorage.getItem(key); return item ? JSON.parse(item) : initialValue; }); useEffect(() => { localStorage.setItem(key, JSON.stringify(value)); }, [key, value]); return [value, setValue]; }",
  "const useFetch = (url) => { const [data, setData] = useState(null); const [loading, setLoading] = useState(true); const [error, setError] = useState(null); useEffect(() => { fetch(url).then(res => res.json()).then(setData).catch(setError).finally(() => setLoading(false)); }, [url]); return { data, loading, error }; }",
];

export default function TypingTest({ isOpen, onClose }) {
  const [snippet, setSnippet] = useState('');
  const [input, setInput] = useState('');
  const [startTime, setStartTime] = useState(null);
  const [wpm, setWpm] = useState(0);
  const [accuracy, setAccuracy] = useState(100);
  const [isComplete, setIsComplete] = useState(false);
  const [difficulty, setDifficulty] = useState('all'); // all, short, medium, long
  const inputRef = useRef(null);

  // Get color scheme based on difficulty
  const getColorScheme = () => {
    switch(difficulty) {
      case 'short':
        return {
          primary: 'text-green-400',
          border: 'border-green-500/30',
          borderFocus: 'focus:border-green-500',
          bg: 'bg-green-500/30',
          button: 'bg-green-500 hover:bg-green-600'
        };
      case 'medium':
        return {
          primary: 'text-yellow-400',
          border: 'border-yellow-500/30',
          borderFocus: 'focus:border-yellow-500',
          bg: 'bg-yellow-500/30',
          button: 'bg-yellow-500 hover:bg-yellow-600'
        };
      case 'long':
        return {
          primary: 'text-red-400',
          border: 'border-red-500/30',
          borderFocus: 'focus:border-red-500',
          bg: 'bg-red-500/30',
          button: 'bg-red-500 hover:bg-red-600'
        };
      default:
        return {
          primary: 'text-purple-400',
          border: 'border-purple-500/30',
          borderFocus: 'focus:border-purple-500',
          bg: 'bg-purple-500/30',
          button: 'bg-purple-500 hover:bg-purple-600'
        };
    }
  };

  const colors = getColorScheme();

  const getFilteredSnippets = () => {
    if (difficulty === 'short') {
      return CODE_SNIPPETS.filter(s => s.length < 50);
    } else if (difficulty === 'medium') {
      return CODE_SNIPPETS.filter(s => s.length >= 50 && s.length < 150);
    } else if (difficulty === 'long') {
      return CODE_SNIPPETS.filter(s => s.length >= 150);
    }
    return CODE_SNIPPETS;
  };

  useEffect(() => {
    if (isOpen) {
      const filtered = getFilteredSnippets();
      setSnippet(filtered[Math.floor(Math.random() * filtered.length)]);
      setInput('');
      setStartTime(null);
      setIsComplete(false);
      setWpm(0);
      setAccuracy(100);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, difficulty]);

  useEffect(() => {
    if (input.length === 1 && !startTime) {
      setStartTime(Date.now());
    }

    if (input === snippet && snippet) {
      setIsComplete(true);
      // Play completion sound
      try {
        const audio = new Audio('/typewin.mp3');
        audio.volume = 0.4;
        audio.play().catch(e => console.log('Audio play failed:', e));
      } catch (error) {
        console.log('Sound error:', error);
      }
      if (startTime) {
        const timeInMinutes = (Date.now() - startTime) / 60000;
        const words = snippet.split(' ').length;
        setWpm(Math.round(words / timeInMinutes));
      }
    }

    // Calculate accuracy
    if (input.length > 0) {
      let correct = 0;
      for (let i = 0; i < input.length; i++) {
        if (input[i] === snippet[i]) correct++;
      }
      setAccuracy(Math.round((correct / input.length) * 100));
    }
  }, [input, snippet, startTime]);

  const resetTest = () => {
    const filtered = getFilteredSnippets();
    setSnippet(filtered[Math.floor(Math.random() * filtered.length)]);
    setInput('');
    setStartTime(null);
    setIsComplete(false);
    setWpm(0);
    setAccuracy(100);
    inputRef.current?.focus();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 z-[100] flex items-center justify-center backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.8, opacity: 0 }}
          onClick={e => e.stopPropagation()}
          className="bg-gradient-to-br from-gray-900 to-gray-800 p-8 rounded-2xl shadow-2xl border-2 border-purple-500/30 max-w-3xl w-full mx-4"
        >
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-2xl font-bold text-white mb-2 flex items-center gap-2">
                <svg className="w-7 h-7 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Code Typing Test
              </h2>
              
              {/* Difficulty Selector */}
              <div className="flex gap-2">
                {['all', 'short', 'medium', 'long'].map(level => {
                  let selectedClass = '';
                  let unselectedClass = '';
                  
                  if (level === 'short') {
                    selectedClass = 'bg-green-500 text-white border-green-500';
                    unselectedClass = 'bg-gray-700 text-green-400 border-green-500/50 hover:bg-green-500/20 hover:border-green-500';
                  } else if (level === 'medium') {
                    selectedClass = 'bg-yellow-500 text-gray-900 border-yellow-500';
                    unselectedClass = 'bg-gray-700 text-yellow-400 border-yellow-500/50 hover:bg-yellow-500/20 hover:border-yellow-500';
                  } else if (level === 'long') {
                    selectedClass = 'bg-red-500 text-white border-red-500';
                    unselectedClass = 'bg-gray-700 text-red-400 border-red-500/50 hover:bg-red-500/20 hover:border-red-500';
                  } else {
                    selectedClass = 'bg-purple-500 text-white border-purple-500';
                    unselectedClass = 'bg-gray-700 text-purple-400 border-purple-500/50 hover:bg-purple-500/20 hover:border-purple-500';
                  }
                  
                  return (
                    <button
                      key={level}
                      onClick={() => setDifficulty(level)}
                      className={`px-3 py-1 rounded-lg text-xs font-medium transition-all duration-300 border-2 ${
                        difficulty === level
                          ? selectedClass
                          : unselectedClass
                      }`}
                    >
                      {level.charAt(0).toUpperCase() + level.slice(1)}
                    </button>
                  );
                })}
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className={`bg-black/50 p-4 rounded-lg border ${colors.border}`}>
              <p className="text-gray-400 text-sm mb-1">WPM</p>
              <p className={`text-3xl font-bold ${colors.primary}`}>{wpm}</p>
            </div>
            <div className={`bg-black/50 p-4 rounded-lg border ${colors.border}`}>
              <p className="text-gray-400 text-sm mb-1">Accuracy</p>
              <p className={`text-3xl font-bold ${colors.primary}`}>{accuracy}%</p>
            </div>
            <div className={`bg-black/50 p-4 rounded-lg border ${colors.border}`}>
              <p className="text-gray-400 text-sm mb-1">Characters</p>
              <p className={`text-3xl font-bold ${colors.primary}`}>{input.length}/{snippet.length}</p>
            </div>
          </div>

          {/* Code Display */}
          <div className={`bg-black p-6 rounded-lg mb-4 font-mono text-base border ${colors.border} max-h-48 overflow-y-auto`}>
            <div className="whitespace-pre-wrap break-words">
              {snippet.split('').map((char, index) => {
                let color = 'text-gray-600';
                if (index < input.length) {
                  color = input[index] === char ? colors.primary : 'text-red-400';
                }
                if (index === input.length) {
                  color = `text-white ${colors.bg}`;
                }
                return (
                  <span key={index} className={`${color} transition-colors`}>
                    {char}
                  </span>
                );
              })}
            </div>
          </div>

          {/* Input */}
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={e => setInput(e.target.value)}
            disabled={isComplete}
            className={`w-full bg-black/50 text-white p-4 rounded-lg border ${colors.border} ${colors.borderFocus} focus:outline-none font-mono disabled:opacity-50`}
            placeholder="Start typing..."
          />

          {/* Complete Message */}
          {isComplete && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-6 text-center"
            >
              <div className="mb-4">
                <svg className={`w-16 h-16 ${colors.primary} mx-auto`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className={`text-2xl font-bold ${colors.primary} mb-2`}>Complete!</p>
              <p className="text-gray-400 mb-4">WPM: {wpm} • Accuracy: {accuracy}%</p>
              <button
                onClick={resetTest}
                className={`px-6 py-2 ${colors.button} text-white rounded-lg transition-colors font-semibold`}
              >
                Try Another
              </button>
            </motion.div>
          )}

          <p className="text-center text-sm text-gray-400 mt-4">
            Type the code snippet as fast and accurately as you can!
          </p>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
